import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Bell } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface NotificationPermissionBannerProps {
  onClose?: () => void;
}

const NotificationPermissionBanner: React.FC<NotificationPermissionBannerProps> = ({ onClose }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [permissionState, setPermissionState] = useState<NotificationPermission>('default');
  const { toast } = useToast();

  useEffect(() => {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      return;
    }

    const currentPermission = Notification.permission;
    setPermissionState(currentPermission);

    // Show banner if permission is not granted
    // Only hide permanently once permission is actually granted
    if (currentPermission !== 'granted') {
      setShowBanner(true);
    } else {
      // If permission is already granted, check if we should still hide the banner
      const bannerDismissed = localStorage.getItem('notificationBannerDismissed');
      if (bannerDismissed) {
        setShowBanner(false);
      }
    }
  }, []);

  const handleRequestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      
      if (permission === 'granted') {
        toast({
          title: "Notifications Enabled",
          description: "You will now receive important notifications about patient transfers and updates.",
          duration: 3000
        });
        setShowBanner(false);
        // Only save to localStorage when permission is granted
        localStorage.setItem('notificationBannerDismissed', 'true');
      } else if (permission === 'denied') {
        toast({
          title: "Notifications Blocked",
          description: "You can enable notifications in your browser settings if you change your mind.",
          variant: "destructive",
          duration: 5000
        });
        setShowBanner(false);
        // Don't save to localStorage when denied - banner should reappear on refresh
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast({
        title: "Permission Error",
        description: "Unable to request notification permission. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCloseBanner = () => {
    setShowBanner(false);
    // Don't save to localStorage - banner should reappear on page refresh
    // until permission is actually granted
    onClose?.();
  };

  // Don't render if notifications aren't supported or banner shouldn't show
  if (!('Notification' in window) || !showBanner) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 px-6 py-3 relative rounded-lg">
      <div className="flex items-center justify-between mx-auto">
        <div className="flex items-center space-x-3">
          <Bell className="h-5 w-5 text-yellow-600" />
          <div className="flex-1">
            <p className="text-sm text-yellow-800 font-medium">
              Enable notifications to receive important updates about patient transfers and system alerts.
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRequestPermission}
            size="sm"
            className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-3 py-1 h-7"
          >
            Enable Notifications
          </Button>
          <Button
            onClick={handleCloseBanner}
            variant="ghost"
            size="sm"
            className="text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 p-1 h-7 w-7"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionBanner;